import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { PlatformBadge } from "../PlatformBadge";
import { formatCurrency, formatNumber, formatPercent, formatRoas } from "@/lib/formatters";

interface UnifiedAdRow {
  id: string;
  name: string;
  accountName?: string;
  platformSlug?: string;
  platformColor?: string;
  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    purchases: number;
    purchaseValue: number;
    ctr: number;
    cpc: number;
    roas: number;
  };
}

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function UnifiedTopAdsTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<UnifiedAdRow[]>({
    queryKey: ["unified-top-ads", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate, metric: "spend", limit: "10" });
      const res = await fetch(getUrl("top-ads", params));
      if (!res.ok) throw new Error("Failed to fetch top ads");
      return res.json();
    },
    enabled: sourceType === "unified",
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Top Ads (Cross-Platform)" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize} platformSlug="unified">
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-unified-top-ads">No ad data available</div>
      ) : (
        <div className="overflow-auto h-full" data-testid="table-unified-top-ads">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-gray-100">
              <tr>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Ad</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Platform</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Spend</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">ROAS</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Purch.</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">CTR</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">CPC</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((ad) => (
                <tr key={ad.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2 px-3">
                    <span className="font-medium text-gray-900 truncate max-w-[180px] block">{ad.name}</span>
                    {ad.accountName && <span className="text-[10px] text-gray-400 block truncate">{ad.accountName}</span>}
                  </td>
                  <td className="py-2 px-3">
                    {ad.platformSlug && <PlatformBadge platform={ad.platformSlug} size="sm" />}
                  </td>
                  <td className="py-2 px-3 text-gray-700">{formatCurrency(ad.metrics?.spend)}</td>
                  <td className="py-2 px-3 font-medium text-gray-900">{formatRoas(ad.metrics?.roas)}</td>
                  <td className="py-2 px-3 text-gray-700">{formatNumber(ad.metrics?.purchases)}</td>
                  <td className="py-2 px-3 text-gray-700">{formatPercent(ad.metrics?.ctr)}</td>
                  <td className="py-2 px-3 text-gray-700">{formatCurrency(ad.metrics?.cpc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardWidget>
  );
}
